import { useState, useCallback, useEffect, useRef } from 'react';
import { API, Utils } from '@enrich/shared-lib';
import QueryString from 'qs';

const PAGE_SIZE = 25;

// Sortable columns map to Strapi sort paths. Relation paths work because
// /api/to-dos is a plain core controller, so it honours whatever sort we send.
const SORT_FIELDS = {
    student: ['contact.firstName', 'contact.lastName'],
    task: ['task.title'],
    assignee: ['assignee.firstName', 'assignee.lastName'],
    createdAt: ['createdAt'],
};

export const DEFAULT_SORT = { property: 'createdAt', direction: 'desc' };

export const isDefaultSort = (sort) =>
    !sort ||
    (sort.property === DEFAULT_SORT.property &&
        sort.direction === DEFAULT_SORT.direction);

function buildSort(sort) {
    const { property, direction } = sort ?? DEFAULT_SORT;
    const fields = SORT_FIELDS[property] ?? SORT_FIELDS[DEFAULT_SORT.property];
    const dir = direction === 'asc' ? 'asc' : 'desc';
    return fields.map((field) => `${field}:${dir}`);
}

function sortTodosCurrentClientsFirst(todos) {
    return [...(todos ?? [])].sort((a, b) => {
        const aIsActive = a.contact?.enrichProgramStatus === 'active' ? 0 : 1;
        const bIsActive = b.contact?.enrichProgramStatus === 'active' ? 0 : 1;
        return aIsActive - bIsActive;
    });
}

function assignmentFilter(assignment) {
    if (assignment === 'assigned') {
        return { assignee: { id: { $notNull: true } } };
    }
    if (assignment === 'unassigned') {
        return { assignee: { id: { $null: true } } };
    }
    return null;
}

function assigneeSearchFilter(search) {
    const term = (search ?? '').trim();
    if (!term) return null;
    return {
        $or: [
            { assignee: { firstName: { $containsi: term } } },
            { assignee: { lastName: { $containsi: term } } },
            { assignee: { email: { $containsi: term } } },
        ],
    };
}

function contractClientFilter(contractClientsOnly) {
    if (!contractClientsOnly) return null;
    return {
        candidacy: {
            job: {
                company: {
                    isContractClient: { $eq: true },
                },
            },
        },
    };
}

function buildTodosQuery({
    assigneeId,
    assignment = 'all',
    search,
    contractClientsOnly = false,
    sort = DEFAULT_SORT,
    page,
}) {
    // Only incomplete tasks. Completed tasks drop off the dashboard
    // immediately and are never refetched. Wrapped in $and so the assignee
    // search $or group can coexist without key collisions.
    const and = [{ completed: false }, { task: { internalType: 'coach' } }];

    if (assigneeId) {
        and.push({ assignee: assigneeId });
    } else {
        const assignment_ = assignmentFilter(assignment);
        if (assignment_) and.push(assignment_);
        const searchFilter = assigneeSearchFilter(search);
        if (searchFilter) and.push(searchFilter);
    }
    const clientFilter = contractClientFilter(contractClientsOnly);
    if (clientFilter) and.push(clientFilter);

    return QueryString.stringify({
        filters: { $and: and },
        populate: {
            candidacy: { populate: ['job'] },
            contact: { populate: ['user'] },
            assignee: { fields: ['firstName', 'lastName', 'email'] },
            message: true,
            task: true,
        },
        sort: buildSort(sort),
        pagination: {
            page,
            pageSize: PAGE_SIZE,
        },
    });
}

export function buildInitialTodosQuery({ assigneeId } = {}) {
    return buildTodosQuery({ assigneeId, page: 1 });
}

export function useTodosInfinite(
    initialTodos,
    initialPagination,
    {
        assigneeId,
        assignment = 'all',
        search = '',
        contractClientsOnly = false,
        sort = DEFAULT_SORT,
    } = {},
) {
    const [allTodos, setAllTodos] = useState(() =>
        sortTodosCurrentClientsFirst(Utils.deepFlatten(initialTodos ?? [])),
    );
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(() =>
        initialPagination
            ? initialPagination.page < initialPagination.pageCount
            : false,
    );

    // Any filter being on means the loader data (which is always unfiltered
    // page 1) no longer matches what the table claims to be showing. A
    // revalidate() after a task action must not silently wipe the filters.
    const filtersActive =
        assignment !== 'all' ||
        (search ?? '').trim() !== '' ||
        contractClientsOnly ||
        !isDefaultSort(sort);

    // Bumped whenever the active filters change. Any in-flight request whose
    // token no longer matches is stale and its response is discarded.
    const requestIdRef = useRef(0);

    const initialTodosRef = useRef(initialTodos);
    useEffect(() => {
        if (initialTodosRef.current === initialTodos) return;
        initialTodosRef.current = initialTodos;
        if (filtersActive) return;
        setAllTodos(
            sortTodosCurrentClientsFirst(Utils.deepFlatten(initialTodos ?? [])),
        );
        setPage(1);
        setHasMore(
            initialPagination
                ? initialPagination.page < initialPagination.pageCount
                : false,
        );
    }, [initialTodos, initialPagination, filtersActive]);

    // Refetch page 1 from the API when the assignment filter or search term
    // changes. Skip the first render so the loader-provided data is used as-is.
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        // Invalidate any in-flight loadMore from the previous filter set.
        requestIdRef.current += 1;
        let isActive = true;
        (async () => {
            setLoading(true);
            try {
                const query = buildTodosQuery({
                    assigneeId,
                    assignment,
                    search,
                    contractClientsOnly,
                    sort,
                    page: 1,
                });
                const response = await API.get(`/api/to-dos?${query}`);
                if (!isActive) return;
                const todos = Utils.deepFlatten(response.data.data ?? []);
                const meta = response.data.meta?.pagination;
                setAllTodos(
                    isDefaultSort(sort)
                        ? sortTodosCurrentClientsFirst(todos)
                        : todos,
                );
                setPage(1);
                setHasMore(meta ? 1 < meta.pageCount : false);
            } catch (error) {
                console.log({ filterTodosError: error });
            }
            if (isActive) setLoading(false);
        })();
        return () => {
            isActive = false;
        };
    }, [assignment, assigneeId, search, contractClientsOnly, sort]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;
        const requestId = requestIdRef.current;
        setLoading(true);
        try {
            const nextPage = page + 1;
            const query = buildTodosQuery({
                assigneeId,
                assignment,
                search,
                contractClientsOnly,
                sort,
                page: nextPage,
            });
            const response = await API.get(`/api/to-dos?${query}`);
            // Filters changed while this page was in flight: these rows
            // belong to the old query, so drop them.
            if (requestId !== requestIdRef.current) return;
            const newTodos = Utils.deepFlatten(response.data.data ?? []);
            const meta = response.data.meta?.pagination;
            setAllTodos((prev) =>
                isDefaultSort(sort)
                    ? sortTodosCurrentClientsFirst([...prev, ...newTodos])
                    : [...prev, ...newTodos],
            );
            setPage(nextPage);
            setHasMore(meta ? nextPage < meta.pageCount : false);
        } catch (error) {
            console.log({ loadMoreTodosError: error });
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [
        loading,
        hasMore,
        page,
        assigneeId,
        assignment,
        search,
        contractClientsOnly,
        sort,
    ]);

    return { allTodos, loadMore, loading, hasMore };
}
